/**
 * LoadingSpinner Component
 * Reusable loading skeleton for Suspense fallbacks
 */

import { Skeleton } from './ui/skeleton'
import { ListSkeleton } from './ui/loading-skeletons'

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg'
  message?: string
  fullScreen?: boolean
  variant?: 'list' | 'card' | 'page' // Type of skeleton to show
}

export function LoadingSpinner({
  size = 'md',
  message,
  fullScreen = true,
  variant = 'list'
}: LoadingSpinnerProps) {
  const containerClasses = fullScreen
    ? 'min-h-screen flex items-center justify-center bg-background p-8'
    : 'flex items-center justify-center p-8'

  // For list/card variants, show skeleton pattern
  if (variant === 'list') {
    return (
      <div className={containerClasses}>
        <div className="w-full max-w-2xl space-y-4">
          {message && <Skeleton className="h-6 w-48 mb-6" />}
          <ListSkeleton count={size === 'sm' ? 3 : size === 'lg' ? 10 : 5} />
        </div>
      </div>
    )
  }

  if (variant === 'page') {
    return (
      <div className={containerClasses}>
        <div className="w-full max-w-4xl space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
          </div>
        </div>
      </div>
    )
  }

  // Default: Simple skeleton
  return (
    <div className={containerClasses}>
      <div className="w-full max-w-2xl space-y-3">
        {message && <Skeleton className="h-4 w-32 mb-4" />}
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  )
}
